import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback} from 'react';

import {api} from 'app/modules/Remote';
import type {ApiPaymentMethod, ApiStripeClientSecret} from 'app/modules/Remote/Organization';

/**
 * An organization with no Stripe customer isn't an error state — customer setup is an internal
 * admin task — so the 402 those routes return resolves to a value the UI can render.
 */
export type PaymentMethodsResult =
  | {kind: 'paymentMethods'; paymentMethods: ApiPaymentMethod[]}
  | {kind: 'notConfigured'};

/** The same 402-is-a-state treatment for the cart element's customer session. */
export type CustomerSessionResult =
  | {kind: 'session'; session: ApiStripeClientSecret}
  | {kind: 'notConfigured'};

export const BILLING_NOT_CONFIGURED_MESSAGE =
  "Billing isn't set up for this organization yet. Contact support to enable online payments.";

export const PAYMENT_OPTIONS_LOAD_ERROR =
  'We couldn’t load payment options. Reload the page and try again.';

const paymentMethodsKey = (organizationId: string) => ['paymentMethods', organizationId];
const customerSessionKey = (organizationId: string) => [
  'paymentMethodCustomerSession',
  organizationId,
];

function isBillingNotConfigured(thrown: unknown): boolean {
  return (thrown as {status?: number} | null)?.status === 402;
}

function hasUnverifiedBankAccount(result: PaymentMethodsResult | undefined): boolean {
  return (
    result?.kind === 'paymentMethods' &&
    result.paymentMethods.some(
      (paymentMethod) =>
        paymentMethod.type === 'us_bank_account' && paymentMethod.pendingVerification !== null
    )
  );
}

export function usePaymentMethods(
  organizationId: string,
  enabled = true,
  {retry}: {retry?: number} = {}
) {
  return useQuery<PaymentMethodsResult>({
    queryKey: paymentMethodsKey(organizationId),
    enabled,
    ...(retry === undefined ? {} : {retry}),
    // Microdeposits are verified on Stripe's page in another tab, so returning to this one is when
    // an unverified account may have changed.
    refetchOnWindowFocus: (query) => (hasUnverifiedBankAccount(query.state.data) ? 'always' : true),
    queryFn: async () => {
      try {
        return {
          kind: 'paymentMethods',
          paymentMethods: await api.organizations.paymentMethods.list(organizationId),
        };
      } catch (thrown) {
        if (isBillingNotConfigured(thrown)) {
          return {kind: 'notConfigured'};
        }
        throw thrown;
      }
    },
  });
}

/**
 * A SetupIntent is single-use, so this is scoped to one open dialog: `enabled` turns it on when the
 * dialog opens and the zero cache time means the next open asks for a fresh one.
 */
export function usePaymentMethodSetupIntent(organizationId: string, enabled: boolean) {
  return useQuery<ApiStripeClientSecret>({
    queryKey: ['paymentMethodSetupIntent', organizationId],
    enabled,
    gcTime: 0,
    staleTime: 0,
    retry: false,
    queryFn: () => api.organizations.paymentMethods.createSetupIntent(organizationId),
  });
}

export function useRemovePaymentMethod(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      api.organizations.paymentMethods.remove(organizationId, paymentMethodId),
    onSuccess: () => queryClient.invalidateQueries({queryKey: paymentMethodsKey(organizationId)}),
  });
}

export function useSetDefaultPaymentMethod(organizationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      api.organizations.paymentMethods.setDefault(organizationId, paymentMethodId),
    onSuccess: () => queryClient.invalidateQueries({queryKey: paymentMethodsKey(organizationId)}),
  });
}

/**
 * Scopes the cart's PaymentElement to the organization's Stripe customer, which is what makes its
 * saved cards and the save checkbox appear. Tokenization doesn't depend on the session, so the
 * 30-minute expiry Stripe puts on one costs the saved-card list, not the ability to pay.
 */
export function useCartCustomerSession(organizationId: string, enabled: boolean) {
  return useQuery<CustomerSessionResult>({
    queryKey: customerSessionKey(organizationId),
    enabled,
    retry: false,
    // Every fetch mints a new session, and the PaymentElement remounts on the new secret, which
    // wipes whatever the buyer has typed and can drop the payment handle mid-submit. Stale just
    // short of Stripe's 30-minute expiry, so an element that mounts later never gets a dead one.
    staleTime: 25 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        return {
          kind: 'session',
          session: await api.organizations.paymentMethods.createCustomerSession(organizationId),
        };
      } catch (thrown) {
        if (isBillingNotConfigured(thrown)) {
          return {kind: 'notConfigured'};
        }
        throw thrown;
      }
    },
  });
}

/** Re-reads the organization's cards and leaves the customer session, and so the element, as is. */
export function useRefreshCards(organizationId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({queryKey: paymentMethodsKey(organizationId)});
  }, [queryClient, organizationId]);
}

/**
 * Re-reads the organization's cards and starts a fresh customer session, which remounts the cart
 * element on the customer as it now stands. For when the cards may have changed where this page
 * can't see, such as Settings in another tab.
 */
export function useRefreshCardsAndSession(organizationId: string) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({queryKey: paymentMethodsKey(organizationId)});
    void queryClient.invalidateQueries({queryKey: customerSessionKey(organizationId)});
  }, [queryClient, organizationId]);
}
